"use client";
import React from "react";
import Image from "next/image";
import {
  Center,
  Separator,
  Table,
  Box,
  Flex,
  VStack,
  Stack,
  HStack,
  SimpleGrid,
} from "@chakra-ui/react";
import { Heading, Text } from "@/elements";
import { useHeadingVariant } from "@/theme/hooks/useSectionStyle";
import PricingFooter from "@/components/pricingFooter";
import { BAREMETAL_SERVER_DATA, FOOTER_CONSTANTS } from "./constants";

export const DesktopView = () => {
  return (
    <Box overflowX="auto" w="100%">
      <Table.Root striped width="100%" minWidth="800px">
        <Table.Header px={14}>
          <Table.Row>
            <Table.ColumnHeader fontWeight="bold">Processor</Table.ColumnHeader>
            <Table.ColumnHeader fontWeight="bold">Memory</Table.ColumnHeader>
            <Table.ColumnHeader fontWeight="bold">Base Clock | Boost</Table.ColumnHeader>
            <Table.ColumnHeader fontWeight="bold">Disk</Table.ColumnHeader>
            <Table.ColumnHeader fontWeight="bold">Boot Drives</Table.ColumnHeader>
            <Table.ColumnHeader fontWeight="bold">Price / month</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {BAREMETAL_SERVER_DATA.map((plan, index) => (
            <Table.Row key={index} whiteSpace="nowrap" fontSize="15px" color="#2d3748">
              <Table.Cell>
                <Box display="flex" alignItems="center" gap="6px">
                  <Box h="2rem" w="2rem" position="relative">
                    <Image
                      src={plan.image}
                      alt="image"
                      sizes="38px"
                      style={{ objectFit: "contain" }}
                    />
                  </Box>
                  <Flex direction="column" whiteSpace="nowrap">
                    <Box>{plan.processor}</Box>
                    <Box fontSize="12px" color="text.100">
                      {plan.sku}
                    </Box>
                  </Flex>
                </Box>
              </Table.Cell>
              <Table.Cell>{plan.memory}</Table.Cell>
              <Table.Cell>{plan.baseclock_boost}</Table.Cell>
              <Table.Cell>{plan.disk}</Table.Cell>
              <Table.Cell>{plan.bootDrives}</Table.Cell>
              <Table.Cell>
                <Text fontWeight="700" fontSize="18px" color="primary.200">
                  {plan.price}
                </Text>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

export const MobileView = () => {
  return (
    <SimpleGrid columns={{ base: 1, sm: 2 }} gap={6}>
      {BAREMETAL_SERVER_DATA.map((plan, index) => (
        <VStack
          key={index}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          boxShadow="md"
          gap={4}
          w="100%"
          p={4}
          bg="white"
        >
          <HStack gap={3} align="center" w="full">
            <Box
              h="3rem"
              w="3rem"
              position="relative"
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="md"
              bg="gray.50"
            >
              <Image
                src={plan.image}
                alt="image"
                sizes="48px"
                style={{ objectFit: "contain" }}
                fill
              />
            </Box>
            <Flex flex="1" direction="column">
              <Text fontWeight="bold" fontSize="lg">
                {plan.processor}
              </Text>
              <Text fontSize="sm" color="text.100">
                {plan.sku}
              </Text>
            </Flex>
          </HStack>

          <Separator borderColor="gray.200" />

          <HStack justify="center" w="full">
            <Text fontSize="sm" color="gray.600">
              Base Clock | Boost
            </Text>
            <Text fontWeight="semibold" fontSize="md">
              {plan.baseclock_boost}
            </Text>
          </HStack>

          <Stack direction="row" gap={4} justify="space-between" w="full" flexWrap="wrap">
            <Flex direction="column" flex="1">
              <Text fontSize="sm" color="text.100">
                Memory
              </Text>
              <Text fontWeight="medium" fontSize="md">
                {plan.memory}
              </Text>
            </Flex>
            <Flex direction="column" flex="1">
              <Text fontSize="sm" color="text.100">
                Disk
              </Text>
              <Text fontWeight="medium" fontSize="md">
                {plan.disk}
              </Text>
            </Flex>
          </Stack>

          <Stack direction="row" gap={4} justify="space-between" w="full" flexWrap="wrap">
            <Flex direction="column" flex="1">
              <Text fontSize="sm" color="text.100">
                Boot Drives
              </Text>
              <Text fontWeight="medium" fontSize="md">
                {plan.bootDrives}
              </Text>
            </Flex>
            <Flex direction="column" flex="1">
              <Text fontSize="sm" color="text.100">
                Price / mo
              </Text>
              <Text fontWeight="bold" fontSize="lg" color="primary.200">
                {plan.price}
              </Text>
            </Flex>
          </Stack>
        </VStack>
      ))}
    </SimpleGrid>
  );
};

const BaremetalTable = () => {
  const HeadingVariant = useHeadingVariant();
  return (
    <Box id="baremetal">
      <Heading variant={HeadingVariant} textAlign={"center"} mb={8}>
        Bare Metal Server
      </Heading>
      <Center display={{ md: "none" }} w="full">
        <MobileView />
      </Center>
      <Box display={{ base: "none", md: "block" }}>
        <DesktopView />
      </Box>
      <PricingFooter {...FOOTER_CONSTANTS} />
      <Separator borderWidth="2px" borderColor="#ECECEC" my={10} />
    </Box>
  );
};

export default BaremetalTable;
